<template>
  <div class="bg-gray-50 rounded-xl shadow-lg overflow-hidden hide-scrollbar pt-2">
    <!-- Loading state -->
    <div v-if="isLoading" class="p-8 flex flex-col items-center justify-center">
      <svg class="animate-spin h-10 w-10 text-teal-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      <p class="text-gray-600">Loading metro information...</p>
    </div>

    <!-- Error state -->
    <div v-else-if="error" class="p-8 text-center">
      <div class="mb-6">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
        </svg>
        <p class="text-lg font-medium text-gray-800 mb-2">No metro routes found</p>
        <p class="text-gray-600 text-sm">{{ error }}</p>
      </div>
      <button @click="$emit('retry')" class="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-xl font-medium transition-colors">
        Try Another Search
      </button>
    </div>

    <!-- No results state -->
    <div v-else-if="routes.length === 0" class="p-8 text-center">
      <div class="mb-6">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-16 w-16 mx-auto text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 20a7.962 7.962 0 01-5-1.709M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        <p class="text-lg font-medium text-gray-800 mb-2">No metro routes found</p>
        <p class="text-gray-600 text-sm">No routes available between {{ formatStationName(from) }} and {{ formatStationName(to) }}</p>
      </div>
      <button @click="$emit('retry')" class="bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-xl font-medium transition-colors">
        Try Another Search
      </button>
    </div>

    <!-- Results list -->
    <div v-else class="py-4 pb-6">
      <!-- Search summary -->
      <div class="bg-teal-500 text-white rounded-lg mb-4">
        <!-- Desktop layout -->
        <div class="hidden md:block p-4">
          <div class="flex items-center justify-between">
            <div>
              <div class="text-sm opacity-80">From</div>
              <div class="font-bold">{{ formatStationName(from) }}</div>
            </div>

            <div class="flex-shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>

            <div>
              <div class="text-sm opacity-80">To</div>
              <div class="font-bold">{{ formatStationName(to) }}</div>
            </div>
          </div>
        </div>

        <!-- Mobile layout -->
        <div class="md:hidden p-4">
          <div class="flex items-center justify-between">
            <!-- “From” block (left-aligned) -->
            <div>
              <div class="text-xs opacity-80">From</div>
              <div class="font-bold text-sm">{{ formatStationName(from) }}</div>
            </div>

            <!-- Arrow centered -->
            <div class="flex-1 flex justify-center">
              <svg xmlns="http://www.w3.org/2000/svg"
                   class="h-4 w-4"
                   fill="none"
                   viewBox="0 0 24 24"
                   stroke="currentColor">
                <path stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </div>

            <!-- “To” block (right-aligned) -->
            <div class="text-right">
              <div class="text-xs opacity-80">To</div>
              <div class="font-bold text-sm">{{ formatStationName(to) }}</div>
            </div>
          </div>
        </div>
      </div>

      <p class="text-gray-600 mb-4">{{ routes.length }} route(s) found</p>

      <div class="space-y-4 mb-2">
        <div
          v-for="(route, index) in routes"
          :key="index"
          class="bg-white rounded-xl shadow-sm overflow-hidden mb-2"
        >
          <!-- Route header -->
          <div class="bg-gray-50 p-4 border-b">
            <div class="flex justify-between items-center">
              <div>
                <span class="bg-teal-100 text-teal-800 text-xs font-medium px-2 py-1 rounded">
                  {{ route.fromStation?.network || 'Kolkata Metro' }}
                </span>
                <span class="ml-2 font-bold">Metro Route</span>
              </div>
              <div class="text-sm text-gray-500">
                {{ route.interchanges === "0" ? 'Direct Route' : route.interchanges }}
              </div>
            </div>
          </div>

          <!-- Route details -->
          <div class="p-4">
            <!-- Journey Overview -->
            <div class="flex justify-between items-start mb-6">
              <!-- Departure Station -->
              <div class="flex-1">
                <div class="font-medium text-lg">{{ formatStationName(from) }}</div>
                <div class="text-sm text-gray-500">{{ route.fromStation?.stationLayout || 'Underground' }}</div>
                <div class="text-xs text-gray-400">{{ route.fromStation?.network || 'Kolkata Metro' }}</div>
              </div>

              <!-- Journey info -->
              <div class="flex-1 text-center">
                <div class="text-sm text-gray-500 mb-1">{{ route.travelTime }}</div>
                <div class="relative">
                  <div class="h-0.5 bg-gray-300 w-full absolute top-1/2 transform -translate-y-1/2"></div>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-gray-500 mx-auto relative" viewBox="0 0 20 20" fill="currentColor">
                    <path fill-rule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clip-rule="evenodd" />
                  </svg>
                </div>
                <div class="text-sm text-gray-500 mt-1">{{ formatDistance(route.travelDistance) }}</div>
              </div>

              <!-- Arrival Station -->
              <div class="flex-1 text-right">
                <div class="font-medium text-lg">{{ formatStationName(to) }}</div>
                <div class="text-sm text-gray-500">{{ route.toStation?.stationLayout || 'Underground' }}</div>
                <div class="text-xs text-gray-400">{{ route.toStation?.network || 'Kolkata Metro' }}</div>
              </div>
            </div>

            <!-- Linear Route Visualization -->
            <div v-if="route.routeSummary && route.routeSummary.length > 0" class="mt-6 pt-4 border-t">
              <div class="text-sm font-medium text-gray-700 mb-4">Route Stations</div>

              <!-- Vertical Layout (for all devices) -->
              <div class="relative">
                <!-- Vertical line connecting all stations (centered on dots) -->
                <div class="absolute left-2 top-2 bottom-2 w-0.5 bg-teal-300"></div>

                <!-- Station markers -->
                <div class="space-y-4">
                  <div
                    v-for="(station, stationIndex) in route.routeSummary"
                    :key="stationIndex"
                    class="flex items-center relative z-10"
                  >
                    <!-- Station marker -->
                    <div
                      class="w-4 h-4 rounded-full border-2 border-white shadow-sm mr-4 flex-shrink-0"
                      :class="{
                        'bg-teal-600': stationIndex === 0 || stationIndex === route.routeSummary.length - 1,
                        'bg-teal-400': stationIndex > 0 && stationIndex < route.routeSummary.length - 1
                      }"
                    ></div>

                    <!-- Station name -->
                    <div
                      class="text-sm leading-tight"
                      :class="{
                        'font-medium text-teal-700': stationIndex === 0 || stationIndex === route.routeSummary.length - 1,
                        'text-gray-600': stationIndex > 0 && stationIndex < route.routeSummary.length - 1
                      }"
                    >
                      {{ formatStationName(station) }}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Comprehensive Fare Information -->
            <div class="mt-6 pt-4 border-t">
              <div class="text-sm font-medium text-gray-700 mb-3">Fare Information</div>
              <!-- Desktop: 3 columns -->
              <div class="hidden md:grid grid-cols-3 gap-4">
                <div class="text-center bg-gray-50 rounded-lg p-3">
                  <div class="text-xs text-gray-500 mb-1">Token</div>
                  <div class="font-medium text-teal-600">{{ route.fare?.token || 'N/A' }}</div>
                </div>
                <div v-if="route.fare?.smartcard" class="text-center bg-gray-50 rounded-lg p-3">
                  <div class="text-xs text-gray-500 mb-1">Smart Card</div>
                  <div class="font-medium text-teal-600">{{ route.fare.smartcard }}</div>
                </div>
                <div v-if="route.fare?.concessional" class="text-center bg-gray-50 rounded-lg p-3">
                  <div class="text-xs text-gray-500 mb-1">Concessional</div>
                  <div class="font-medium text-teal-600">{{ route.fare.concessional }}</div>
                </div>
              </div>
              <!-- Mobile: Stacked layout -->
              <div class="md:hidden space-y-3">
                <div class="flex justify-between items-center bg-gray-50 rounded-lg p-3">
                  <span class="text-sm text-gray-600">Token Fare</span>
                  <span class="font-medium text-teal-600">{{ route.fare?.token || 'N/A' }}</span>
                </div>
                <div v-if="route.fare?.smartcard" class="flex justify-between items-center bg-gray-50 rounded-lg p-3">
                  <span class="text-sm text-gray-600">Smart Card</span>
                  <span class="font-medium text-teal-600">{{ route.fare.smartcard }}</span>
                </div>
                <div v-if="route.fare?.concessional" class="flex justify-between items-center bg-gray-50 rounded-lg p-3">
                  <span class="text-sm text-gray-600">Concessional</span>
                  <span class="font-medium text-teal-600">{{ route.fare.concessional }}</span>
                </div>
              </div>
            </div>

            <!-- Journey Details -->
            <div class="mt-4 pt-4 border-t">
              <div class="text-sm font-medium text-gray-700 mb-3">Journey Details</div>
              <!-- Desktop: 2x2 grid -->
              <div class="hidden md:grid grid-cols-2 gap-4 text-sm">
                <div class="flex justify-between">
                  <span class="text-gray-500">Travel Time:</span>
                  <span class="font-medium">{{ route.travelTime || 'N/A' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Distance:</span>
                  <span class="font-medium">{{ formatDistance(route.travelDistance) }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Total Stations:</span>
                  <span class="font-medium">{{ route.totalStations || 'N/A' }}</span>
                </div>
                <div class="flex justify-between">
                  <span class="text-gray-500">Interchanges:</span>
                  <span class="font-medium">{{ route.interchanges || 'N/A' }}</span>
                </div>
              </div>
              <!-- Mobile: Stacked layout -->
              <div class="md:hidden space-y-3 text-sm">
                <div class="flex justify-between items-center py-2 border-b border-gray-100">
                  <span class="text-gray-600">Travel Time</span>
                  <span class="font-medium text-gray-800">{{ route.travelTime || 'N/A' }}</span>
                </div>
                <div class="flex justify-between items-center py-2 border-b border-gray-100">
                  <span class="text-gray-600">Distance</span>
                  <span class="font-medium text-gray-800">{{ formatDistance(route.travelDistance) }}</span>
                </div>
                <div class="flex justify-between items-center py-2 border-b border-gray-100">
                  <span class="text-gray-600">Total Stations</span>
                  <span class="font-medium text-gray-800">{{ route.totalStations || 'N/A' }}</span>
                </div>
                <div class="flex justify-between items-center py-2">
                  <span class="text-gray-600">Interchanges</span>
                  <span class="font-medium text-gray-800">{{ route.interchanges || 'N/A' }}</span>
                </div>
              </div>
            </div>

            <!-- Station Details -->
            <div class="mt-4 pt-4 border-t">
              <div class="text-sm font-medium text-gray-700 mb-3">Station Information</div>
              <!-- Desktop: Side by side -->
              <div class="hidden md:grid grid-cols-2 gap-4">
                <!-- From Station Details -->
                <div class="bg-gray-50 rounded-lg p-3">
                  <div class="font-medium text-sm text-gray-700 mb-2">{{ formatStationName(from) }}</div>
                  <div class="space-y-1 text-xs text-gray-600">
                    <div v-if="route.fromStation?.network">
                      <span class="font-medium">Network:</span> {{ route.fromStation.network }}
                    </div>
                    <div v-if="route.fromStation?.stationLayout">
                      <span class="font-medium">Layout:</span> {{ route.fromStation.stationLayout }}
                    </div>
                    <div v-if="route.fromStation?.platformType">
                      <span class="font-medium">Platform:</span> {{ trimPlatform(route.fromStation.platformType) }}
                    </div>
                    <div v-if="route.fromStation?.areasAround">
                      <span class="font-medium">Areas:</span> {{ route.fromStation.areasAround }}
                    </div>
                  </div>
                </div>

                <!-- To Station Details -->
                <div class="bg-gray-50 rounded-lg p-3">
                  <div class="font-medium text-sm text-gray-700 mb-2">{{ formatStationName(to) }}</div>
                  <div class="space-y-1 text-xs text-gray-600">
                    <div v-if="route.toStation?.network">
                      <span class="font-medium">Network:</span> {{ route.toStation.network }}
                    </div>
                    <div v-if="route.toStation?.stationLayout">
                      <span class="font-medium">Layout:</span> {{ route.toStation.stationLayout }}
                    </div>
                    <div v-if="route.toStation?.platformType">
                      <span class="font-medium">Platform:</span> {{ trimPlatform(route.toStation.platformType) }}
                    </div>
                    <div v-if="route.toStation?.areasAround">
                      <span class="font-medium">Areas:</span> {{ route.toStation.areasAround }}
                    </div>
                  </div>
                </div>
              </div>

              <!-- Mobile: Stacked layout -->
              <div class="md:hidden space-y-4">
                <!-- From Station Details -->
                <div class="bg-gray-50 rounded-lg p-4">
                  <div class="font-medium text-sm text-gray-700 mb-3 flex items-center">
                    <div class="w-2 h-2 bg-teal-600 rounded-full mr-2"></div>
                    {{ formatStationName(from) }}
                  </div>
                  <div class="space-y-2 text-sm text-gray-600">
                    <div v-if="route.fromStation?.network" class="flex justify-between">
                      <span class="text-gray-500">Network:</span>
                      <span class="font-medium">{{ route.fromStation.network }}</span>
                    </div>
                    <div v-if="route.fromStation?.stationLayout" class="flex justify-between">
                      <span class="text-gray-500">Layout:</span>
                      <span class="font-medium">{{ route.fromStation.stationLayout }}</span>
                    </div>
                    <div v-if="route.fromStation?.platformType" class="flex justify-between">
                      <span class="text-gray-500">Platform:</span>
                      <span class="font-medium">{{ trimPlatform(route.fromStation.platformType) }}</span>
                    </div>
                    <div v-if="route.fromStation?.areasAround" class="pt-1">
                      <span class="text-gray-500 text-xs">Areas Around:</span>
                      <div class="font-medium text-xs mt-1">{{ route.fromStation.areasAround }}</div>
                    </div>
                  </div>
                </div>

                <!-- To Station Details -->
                <div class="bg-gray-50 rounded-lg p-4">
                  <div class="font-medium text-sm text-gray-700 mb-3 flex items-center">
                    <div class="w-2 h-2 bg-teal-600 rounded-full mr-2"></div>
                    {{ formatStationName(to) }}
                  </div>
                  <div class="space-y-2 text-sm text-gray-600">
                    <div v-if="route.toStation?.network" class="flex justify-between">
                      <span class="text-gray-500">Network:</span>
                      <span class="font-medium">{{ route.toStation.network }}</span>
                    </div>
                    <div v-if="route.toStation?.stationLayout" class="flex justify-between">
                      <span class="text-gray-500">Layout:</span>
                      <span class="font-medium">{{ route.toStation.stationLayout }}</span>
                    </div>
                    <div v-if="route.toStation?.platformType" class="flex justify-between">
                      <span class="text-gray-500">Platform:</span>
                      <span class="font-medium">{{ trimPlatform(route.toStation.platformType) }}</span>
                    </div>
                    <div v-if="route.toStation?.areasAround" class="pt-1">
                      <span class="text-gray-500 text-xs">Areas Around:</span>
                      <div class="font-medium text-xs mt-1">{{ route.toStation.areasAround }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Service Operating Hours -->
            <div v-if="route.firstMetro || route.lastMetro" class="mt-4 pt-4 border-t">
              <div class="text-sm font-medium text-gray-700 mb-3">Service Operating Hours</div>
              <div class="bg-teal-50 rounded-lg p-3">
                <div class="text-xs text-teal-600 mb-2 font-medium">Daily Metro Service Schedule</div>
                <!-- Desktop: Side by side -->
                <div class="hidden md:grid grid-cols-2 gap-4 text-sm">
                  <div v-if="route.firstMetro" class="flex justify-between">
                    <span class="text-gray-600">First Metro:</span>
                    <span class="font-medium text-teal-700">{{ route.firstMetro }}</span>
                  </div>
                  <div v-if="route.lastMetro" class="flex justify-between">
                    <span class="text-gray-600">Last Metro:</span>
                    <span class="font-medium text-teal-700">{{ route.lastMetro }}</span>
                  </div>
                </div>
                <!-- Mobile: Stacked layout -->
                <div class="md:hidden space-y-2 text-sm">
                  <div v-if="route.firstMetro" class="flex justify-between items-center">
                    <span class="text-gray-600">First Metro</span>
                    <span class="font-medium text-teal-700">{{ route.firstMetro }}</span>
                  </div>
                  <div v-if="route.lastMetro" class="flex justify-between items-center">
                    <span class="text-gray-600">Last Metro</span>
                    <span class="font-medium text-teal-700">{{ route.lastMetro }}</span>
                  </div>
                </div>
                <div class="text-xs text-teal-500 mt-3 italic leading-relaxed">
                  * These are the first and last metro timings for the day, not your specific journey times
                </div>
              </div>
            </div>

            <!-- Route Map -->
            <div v-if="route.routeMap" class="mt-4 pt-4 border-t">
              <div class="text-sm font-medium text-gray-700 mb-2">Route Map</div>

              <!-- Normal map (shown when not zoomed) -->
              <div v-if="zoomedMapIndex !== index" class="relative">
                <img
                  :src="route.routeMap"
                  alt="Metro Route Map"
                  class="w-full rounded-lg shadow-sm cursor-pointer transition-all duration-300"
                  @click="zoomMap(index)"
                />
                <div class="absolute bottom-2 right-2 bg-white bg-opacity-70 text-gray-700 rounded-full p-1 md:p-2">
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 md:h-5 md:w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M5 8a1 1 0 011-1h1V6a1 1 0 012 0v1h1a1 1 0 110 2H9v1a1 1 0 11-2 0V9H6a1 1 0 01-1-1z" />
                    <path fill-rule="evenodd" d="M2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8zm6-4a4 4 0 100 8 4 4 0 000-8z" clip-rule="evenodd" />
                  </svg>
                </div>
                <!-- Mobile tap hint -->
                <div class="md:hidden absolute bottom-2 left-2 bg-teal-500 text-white text-xs px-2 py-1 rounded-full opacity-75">
                  Tap to zoom
                </div>
              </div>

              <!-- Zoomed map (shown when zoomed) -->
              <div v-else class="fixed inset-0 z-50 bg-black bg-opacity-80 flex items-center justify-center p-2 md:p-4">
                <div class="relative max-w-4xl w-full">
                  <img
                    :src="route.routeMap"
                    alt="Metro Route Map"
                    class="w-full rounded-lg shadow-lg"
                  />
                  <button
                    @click="closeZoomedMap"
                    class="absolute top-2 right-2 bg-white rounded-full p-2 md:p-3 shadow-lg text-gray-700 hover:text-red-500 transition-colors touch-manipulation"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 md:h-6 md:w-6" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                    </svg>
                  </button>
                  <!-- Mobile: Pinch to zoom hint -->
                  <div class="md:hidden absolute bottom-2 left-2 bg-white bg-opacity-80 text-gray-700 text-xs px-2 py-1 rounded">
                    Pinch to zoom
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'MetroResultsComponent',
  props: {
    from: {
      type: String,
      required: true
    },
    to: {
      type: String,
      required: true
    },
    routes: {
      type: Array,
      default: () => []
    },
    isLoading: {
      type: Boolean,
      default: false
    },
    error: {
      type: String,
      default: null
    }
  },
  data() {
    return {
      zoomedMapIndex: null // Track which map is currently zoomed
    };
  },
  methods: {
    formatStationName(stationName) {
      if (!stationName) return '';
      return stationName
        .split('-')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    formatDistance(distance) {
      if (!distance || distance === 'N/A') return 'N/A';
      // Convert to number and divide by 100000 to get kilometers
      const numericDistance = parseFloat(distance);
      if (isNaN(numericDistance)) return distance;
      const distanceInKm = numericDistance / 100000;
      return `${distanceInKm.toFixed(2)} km`;
    },
    trimPlatform(platformType) {
      if (!platformType) return '';
      // Remove emoji (and anything after) to keep just the platform description
      return platformType.replace(/[\u{1F300}-\u{1FAFF}].*$/u, '').trim();
    },
    zoomMap(index) {
      this.zoomedMapIndex = index;
    },
    closeZoomedMap() {
      this.zoomedMapIndex = null;
    }
  }
}
</script>

<style scoped>
.hide-scrollbar {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE/Edge */
}
.hide-scrollbar::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

img {
  transition: transform 0.3s ease;
}
img:hover {
  transform: scale(1.02);
}

.fixed {
  animation: fadeIn 0.3s ease;
}
@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

button.hover\:text-red-500:hover {
  transform: scale(1.1);
}
</style>
